import React, { useCallback, useEffect, useState } from "react";
import { Plus, MapPin } from "lucide-react";

import { Button } from "src/components/ui/button";
import { useAuth } from "src/contexts/AuthContext";
import { useToast } from "src/components/ui/toast";
import { fetchSiteVisits } from "src/features/site_visits/api/siteVisitsApi";
import { VisitCard } from "src/features/site_visits/components/VisitCard";
import { ScheduleVisitModal } from "src/features/site_visits/components/ScheduleVisitModal";

// Site-visit timeline for a single lead, shown on the Lead detail page.
export function LeadSiteVisitsPanel({ lead }) {
  const { can } = useAuth();
  const { toast } = useToast();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const load = useCallback(() => {
    if (!lead?.id) return;
    setLoading(true);
    fetchSiteVisits({ lead_id: lead.id })
      .then(setVisits)
      .catch(() => toast({ variant: "destructive", title: "Couldn't load site visits" }))
      .finally(() => setLoading(false));
  }, [lead?.id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  function handleChanged(updated) {
    if (!updated) return load();
    setVisits((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
  }

  const canCreate = can("site_visits", "create");

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Site visits</h3>
        {canCreate && (
          <Button size="sm" variant="outline" onClick={() => setScheduleOpen(true)}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Schedule
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
      ) : visits.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center">
          <MapPin className="mx-auto h-7 w-7 text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">No site visits scheduled for this lead.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {visits.map((v) => (
            <VisitCard key={v.id} visit={v} onChanged={handleChanged} />
          ))}
        </div>
      )}

      <ScheduleVisitModal
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        lead={lead}
        onScheduled={() => load()}
      />
    </div>
  );
}
